import React, { FC, useCallback, useMemo } from 'react';
import {
  faDownload,
  faEye,
  Icon,
  usePreviewModal,
  CustomColumns,
  CustomTable,
} from '@/ui/components';
import { StyledLink, StyledLinkContainer } from '@/ui/globalStyles';
import { Document } from '@/types';
import { useTranslate } from '@/translations';
import { styled } from 'styled-components';

const Title = styled.h6`
  font-size: 20px !important;
  padding: 0 0 12px 8px;
  margin: 0;
  font-weight: 500;
  line-height: 1.2;
`;

export const RelatedDocumentsDataGrid: FC<{
  data: Document;
  documents: Document[];
}> = ({ data, documents }) => {
  const { t } = useTranslate();
  const { openPreviewModal, RenderPreviewModal } = usePreviewModal();

  const getFormattedStore = useCallback(
    (
      store: Document[],
      item: Document,
    ): { filter: (string | number[])[]; store: Document[] } => ({
      store,
      filter: item.relatedDocuments?.length
        ? ['documentId', 'contains', item.relatedDocuments]
        : [],
    }),
    [],
  );

  const dataSource = useMemo(
    () => getFormattedStore(documents, data),
    [getFormattedStore, documents, data],
  );

  const columns: CustomColumns<Document> = useMemo(
    () => [
      { dataIndex: 'documentType', title: 'Document Type' },
      { dataIndex: 'entity', title: 'Entity' },
      { dataIndex: 'entityReference', title: 'Entity Reference' },
      { dataIndex: 'origin', title: 'Origin' },
      { dataIndex: 'destination', title: 'Destination' },
      {
        dataIndex: 'documentId',
        title: 'Actions',
        render: (_, record) => (
          <StyledLinkContainer>
            <StyledLink
              onClick={() =>
                openPreviewModal({
                  src: record.fileUrl,
                  fileName: record.fileName,
                })
              }
              aria-label="View"
            >
              <Icon icon={faEye} />
            </StyledLink>
            <a
              href={record.fileUrl}
              download={record.fileName}
              aria-label="Download"
            >
              <Icon icon={faDownload} />
            </a>
          </StyledLinkContainer>
        ),
      },
    ],
    [openPreviewModal],
  );

  return (
    <>
      <Title>{t('associatedDocuments')}</Title>

      <CustomTable
        dataSource={dataSource.store}
        rowKey="documentId"
        columns={columns}
      />

      <RenderPreviewModal />
    </>
  );
};
